// 261010 P9D D9 — hàm THUẦN (0 antd) cho drawer «Công cụ plan» (P8f, trừ thẻ Chạy chặng 1/2 — I7). Lint / judge / chat / đổi kiểu prompt dùng route CŨ, không bọc sổ L1.
import type { BatchEditOp, FilmEvent, FilmPromptMode, FilmRunningJob } from '../../types/film';
import type { WorkflowStepKey } from '../../types/filmWorkflow';
import { planWriteKindsOf, planWriteLabelOf } from './workflowActionPolicy';

// ───────────── Trợ lý: lọc nút hành động ─────────────
export type ChatActionPlan = { kind: 'RUN' } | { kind: 'GOTO'; step: WorkflowStepKey; label: string } | { kind: 'UNKNOWN' };
const GOTO: Record<string, { step: WorkflowStepKey; label: string }> = {
  'run-author-dialogue': { step: 'SCRIPT', label: 'Mở bước 1 — Phân đoạn' },
  'run-durations': { step: 'SCRIPT', label: 'Mở bước 1 — Phân đoạn' },
  'run-fill-narration': { step: 'SCRIPT', label: 'Mở bước 1 — Phân đoạn' },
  'run-breakdown-locations': { step: 'ASSETS', label: 'Mở bước 2 — Tài sản' },
  'run-prepare-references': { step: 'ASSETS', label: 'Mở bước 2 — Tài sản' },
  'run-gen-location-plates': { step: 'ASSETS', label: 'Mở bước 2 — Tài sản' },
  'run-directing': { step: 'DIRECTING', label: 'Mở bước 3 — Đạo diễn' },
  'run-continuity': { step: 'DIRECTING', label: 'Mở bước 3 — Đạo diễn' },
  'run-author-prompts': { step: 'PROMPT', label: 'Mở bước 7 — Prompt' },
  'gen-event-clip': { step: 'VIDEO', label: 'Mở bước 8 — Video' },
  'run-assemble': { step: 'VIDEO', label: 'Mở bước 8 — Video' },
};
/** Pass lẻ vòng qua cổng duyệt (không có hash/duyệt, `script-passes` còn gen ảnh) ⇒ KHÔNG chạy từ chat; chỉ lint + judge chạy được, còn lại dẫn tới bước. */
export function chatActionPlanOf(id: string): ChatActionPlan {
  if (id === 'run-lint' || id === 'run-judge') return { kind: 'RUN' };
  const g = GOTO[id];
  return g ? { kind: 'GOTO', step: g.step, label: g.label } : { kind: 'UNKNOWN' };
}

const STRUCT = new Set(['splitEvent', 'mergeNext', 'insertAfter', 'deleteEvent']);
/** Op cấu trúc (cắt/gộp/chèn/xoá video) cần id tạm của nháp UI cũ ⇒ khung mới không áp từ chat; dùng thanh «Cấu trúc video» ở bước 1. */
export function splitStructOps(ops: BatchEditOp[]): { apply: BatchEditOp[]; skipped: BatchEditOp[] } {
  return { apply: ops.filter((o) => !STRUCT.has(o.type)), skipped: ops.filter((o) => STRUCT.has(o.type)) };
}

/** Áp đề xuất = ghi thẳng plan (batch-edit) ⇒ khoá khi có lượt AI ghi plan đang chạy. Clip một video không khoá. */
export function toolsApplyBlockReason(jobs: Array<Pick<FilmRunningJob, 'type'>>): string | null {
  const kinds = planWriteKindsOf(jobs);
  return kinds.length ? `Đang chạy lượt AI trên cả plan (${kinds.map(planWriteLabelOf).join(', ')}) — đợi xong rồi áp dụng.` : null;
}

export function describeOpVi(op: BatchEditOp, orderOf: Map<string, number>, shotOrderOf: Map<string, number>): string {
  const ev = (id: string) => `video ${orderOf.get(id) ?? '?'}`;
  switch (op.type) {
    case 'updateShot': return `Chỉnh camera một shot của video ${shotOrderOf.get((op as { shotId: string }).shotId) ?? '?'}`;
    case 'replaceDialogue': { const n = (op as { lines?: unknown[] }).lines?.length ?? 0; return `Viết lại thoại của ${ev(op.eventId)} (${n} dòng)`; }
    case 'updateEvent': return `Viết lại hành động của ${ev(op.eventId)}`;
    case 'updatePrompt': return `Viết lại prompt của ${ev(op.eventId)}`;
    default: return 'Thao tác chưa hỗ trợ';
  }
}

// ───────────── Kết quả chấm (chỉ giữ trong phiên) ─────────────
export const JUDGE_CRITERIA: Array<[string, string]> = [
  ['segmentation', 'Chia video'], ['coverage', 'Phủ nguồn'], ['dialogue', 'Thoại'], ['fidelity', 'Trung thành nguồn'], ['spine', 'Mạch truyện'],
];
export function judgeBars(scores: Record<string, number> | null | undefined): Array<{ key: string; label: string; value: number; pct: number }> {
  const s = scores ?? {};
  return JUDGE_CRITERIA.filter(([k]) => typeof s[k] === 'number').map(([k, label]) => {
    const value = Math.round(s[k] * 10) / 10;
    return { key: k, label, value, pct: Math.max(0, Math.min(100, Math.round((value / 5) * 100))) };
  });
}

// ───────────── Kiểu prompt cả plan ─────────────
export const PROMPT_MODE_VI: Record<FilmPromptMode, string> = { NATURAL: 'Tự nhiên', TECHNICAL: 'Kỹ thuật' };
export function promptModeImpactText(mode: FilmPromptMode, r: { pinned: number[]; mismatchAfter: number[]; missingAfter: number[]; okAfter: number }): string {
  const parts: string[] = [];
  if (r.mismatchAfter.length) parts.push(`${r.mismatchAfter.length} prompt sẽ lệch kiểu (cần dựng lại)`);
  if (r.pinned.length) parts.push(`${r.pinned.length} prompt đã được ghim kiểu riêng nên không đổi`);
  if (r.missingAfter.length) parts.push(`${r.missingAfter.length} video chưa có prompt`);
  parts.push(`${r.okAfter} prompt khớp`);
  const head = `Đổi cả plan sang kiểu ${PROMPT_MODE_VI[mode]}: `;
  return r.mismatchAfter.length ? `${head}${parts.join(' · ')}.` : `${head}không prompt nào bị lệch · ${parts.filter((p) => !/lệch/.test(p)).join(' · ')}.`;
}

const ENGINE_LABEL: Record<string, string> = { SEEDANCE_20: 'Seedance 2.0', SEEDANCE_25: 'Seedance 2.5' };
export function engineRowsOf(events: Array<Pick<FilmEvent, 'id' | 'eventOrder' | 'videoEngine' | 'genDurationS' | 'effectivePromptMode' | 'authorPrompt'>>, planEngine?: string | null): Array<{
  id: string; order: number; engineLabel: string; mismatch: boolean; durationS: number | null; modeLabel: string; hasPrompt: boolean;
}> {
  return [...events].sort((a, b) => a.eventOrder - b.eventOrder).map((e) => ({
    id: e.id, order: e.eventOrder,
    engineLabel: ENGINE_LABEL[String(e.videoEngine ?? '')] ?? '—',
    mismatch: !!planEngine && !!e.videoEngine && e.videoEngine !== planEngine,
    durationS: typeof e.genDurationS === 'number' ? e.genDurationS : null,
    modeLabel: e.effectivePromptMode ? PROMPT_MODE_VI[e.effectivePromptMode as FilmPromptMode] ?? '—' : '—',
    hasPrompt: !!String(e.authorPrompt ?? '').trim(),
  }));
}

// ───────────── Thử lại lỗi kiểm / chấm (vòng sửa 1 #4) ─────────────
export type VerifyKind = 'lint' | 'judge';
/** Lỗi `lint` chỉ chạy lại lint (0 POST judge). Lỗi `judge` quay về hộp xác nhận Chấm AI (không gọi ngầm). */
export function verifyRetryOf(kind: VerifyKind): { label: string; action: 'RUN_LINT' | 'ASK_JUDGE' } {
  return kind === 'lint' ? { label: 'Kiểm lại', action: 'RUN_LINT' } : { label: 'Chấm lại', action: 'ASK_JUDGE' };
}

// ───────────── Ngữ cảnh bước cho trợ lý (vòng sửa 1 #7) ─────────────
const STAGE_NAME: Record<string, string> = {
  SCRIPT: 'Phân đoạn', ASSETS: 'Tài sản', DIRECTING: 'Đạo diễn', CINEMATOGRAPHY: 'Quay phim', MAP: 'Sơ đồ', STORYBOARD: 'Storyboard', PROMPT: 'Prompt', VIDEO: 'Video',
};
const STAGE_NO: Record<string, number> = { SCRIPT: 1, ASSETS: 2, DIRECTING: 3, CINEMATOGRAPHY: 4, MAP: 5, STORYBOARD: 6, PROMPT: 7, VIDEO: 8 };
/** `currentStage` gửi `chatbot-suggest`: "N. Tên bước" (cùng dạng UI cũ `index+1. title`), ngắn; không biết bước ⇒ undefined (BE `sanitizeStage` bỏ). */
export function chatStageLabelOf(step: WorkflowStepKey | string | null | undefined): string | undefined {
  const k = String(step ?? '');
  return STAGE_NAME[k] ? `${STAGE_NO[k]}. ${STAGE_NAME[k]}` : undefined;
}
